import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { API } from "@/config";

const EMPTY = {
  name: "", email: "", phone: "", designation: "", department: "", joining_date: "",
  password: "", basic: 0, hra: 0, allowances: 0, overtime_rate: 0,
  pf_applicable: true, esi_applicable: false, pt_amount: 0, bank_account: "", ifsc: "",
};

export const EmployeesSection = () => {
  const [employees, setEmployees] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [form, setForm] = useState(EMPTY);

  const fetchEmployees = async () => {
    const { data } = await axios.get(`${API}/employees`, { withCredentials: true });
    setEmployees(data);
  };

  useEffect(() => {
    fetchEmployees();
  }, []);

  const set = (key) => (e) => setForm({ ...form, [key]: e.target.value });
  const setNum = (key) => (e) => setForm({ ...form, [key]: Number(e.target.value) });

  const addEmployee = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/employees`, form, { withCredentials: true });
      toast.success("Employee created");
      setShowAdd(false);
      setForm(EMPTY);
      fetchEmployees();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create employee");
    }
  };

  const setStatus = async (id, status) => {
    await axios.patch(`${API}/employees/${id}`, { status }, { withCredentials: true });
    toast.success("Status updated");
    fetchEmployees();
  };

  return (
    <div data-testid="employees-section">
      <div className="flex items-center justify-between">
        <p className="text-sm text-muted-foreground">{employees.length} employees</p>
        <Button size="sm" className="gap-1" data-testid="add-employee-button" onClick={() => setShowAdd(true)}>
          <Plus className="h-4 w-4" /> Add Employee
        </Button>
      </div>

      <div className="mt-6 overflow-x-auto rounded-2xl border border-border bg-card shadow-sm">
        <Table data-testid="employees-table">
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Designation</TableHead>
              <TableHead>Contact</TableHead>
              <TableHead>Salary (Basic+HRA+Allow.)</TableHead>
              <TableHead>PF / ESI</TableHead>
              <TableHead>Status</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {employees.map((emp) => (
              <TableRow key={emp.id} data-testid={`employee-row-${emp.id}`}>
                <TableCell className="font-medium">
                  {emp.name}
                  <span className="block text-xs text-muted-foreground">{emp.department || "—"}</span>
                </TableCell>
                <TableCell>{emp.designation}</TableCell>
                <TableCell className="text-sm">
                  {emp.phone}
                  <span className="block text-xs text-muted-foreground">{emp.email}</span>
                </TableCell>
                <TableCell>₹{emp.basic + emp.hra + emp.allowances}</TableCell>
                <TableCell className="text-xs">
                  {emp.pf_applicable ? "PF" : "—"} / {emp.esi_applicable ? "ESI" : "—"}
                </TableCell>
                <TableCell>
                  <Select value={emp.status} onValueChange={(v) => setStatus(emp.id, v)}>
                    <SelectTrigger className="w-28" data-testid={`employee-status-${emp.id}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="active">active</SelectItem>
                      <SelectItem value="inactive">inactive</SelectItem>
                    </SelectContent>
                  </Select>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent data-testid="add-employee-dialog" className="max-h-[90vh] max-w-2xl overflow-y-auto" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-heading">Add Employee</DialogTitle>
          </DialogHeader>
          <form onSubmit={addEmployee} className="grid gap-4" data-testid="add-employee-form">
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Name *</Label>
                <Input required value={form.name} onChange={set("name")} data-testid="emp-name-input" />
              </div>
              <div className="grid gap-2">
                <Label>Email (login) *</Label>
                <Input required type="email" value={form.email} onChange={set("email")} data-testid="emp-email-input" />
              </div>
              <div className="grid gap-2">
                <Label>Phone *</Label>
                <Input required value={form.phone} onChange={set("phone")} data-testid="emp-phone-input" />
              </div>
              <div className="grid gap-2">
                <Label>Password (min 8) *</Label>
                <Input required minLength={8} value={form.password} onChange={set("password")} data-testid="emp-password-input" />
              </div>
              <div className="grid gap-2">
                <Label>Designation *</Label>
                <Input required value={form.designation} onChange={set("designation")} data-testid="emp-designation-input" />
              </div>
              <div className="grid gap-2">
                <Label>Department</Label>
                <Input value={form.department} onChange={set("department")} data-testid="emp-department-input" />
              </div>
              <div className="grid gap-2">
                <Label>Joining Date</Label>
                <Input type="date" value={form.joining_date} onChange={set("joining_date")} data-testid="emp-joining-input" />
              </div>
            </div>
            <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Salary Structure (₹/month)</p>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <div className="grid gap-2">
                <Label>Basic</Label>
                <Input type="number" min="0" value={form.basic} onChange={setNum("basic")} data-testid="emp-basic-input" />
              </div>
              <div className="grid gap-2">
                <Label>HRA</Label>
                <Input type="number" min="0" value={form.hra} onChange={setNum("hra")} data-testid="emp-hra-input" />
              </div>
              <div className="grid gap-2">
                <Label>Allowances</Label>
                <Input type="number" min="0" value={form.allowances} onChange={setNum("allowances")} data-testid="emp-allowances-input" />
              </div>
              <div className="grid gap-2">
                <Label>Overtime ₹/hr</Label>
                <Input type="number" min="0" value={form.overtime_rate} onChange={setNum("overtime_rate")} data-testid="emp-otrate-input" />
              </div>
            </div>
            <div className="flex flex-wrap gap-6">
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.pf_applicable} onCheckedChange={(v) => setForm({ ...form, pf_applicable: !!v })} data-testid="emp-pf-checkbox" />
                PF Applicable (12% of basic, max ₹1,800)
              </label>
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={form.esi_applicable} onCheckedChange={(v) => setForm({ ...form, esi_applicable: !!v })} data-testid="emp-esi-checkbox" />
                ESI Applicable (0.75%, gross ≤ ₹21,000)
              </label>
              <div className="flex items-center gap-2">
                <Label className="text-sm">Prof. Tax ₹</Label>
                <Input type="number" min="0" className="w-24" value={form.pt_amount} onChange={setNum("pt_amount")} data-testid="emp-pt-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="grid gap-2">
                <Label>Bank Account</Label>
                <Input value={form.bank_account} onChange={set("bank_account")} data-testid="emp-bank-input" />
              </div>
              <div className="grid gap-2">
                <Label>IFSC</Label>
                <Input value={form.ifsc} onChange={set("ifsc")} data-testid="emp-ifsc-input" />
              </div>
            </div>
            <Button type="submit" data-testid="emp-submit-button">Create Employee</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};
